import { api, API_ENDPOINTS } from '../lib/api';
import {
  Wallet,
  WalletTransaction,
  RechargeRequest,
  PaginatedResponse,
  WalletHistoryResponse,
  GetWalletHistoryParams,
} from '../types';

/**
 * Query parameters for fetching wallet transactions
 */
export interface GetWalletTransactionsParams {
  page?: number;
  limit?: number;
  type?: 'credit' | 'debit';
}

// Byte-identical to BadRequestException in backend/src/wallet/payment.service.ts
// (verifyPayment, Cashfree order_status === 'active'). Both pending and failed
// are HTTP 400; this string is the only client-side discriminator.
const PENDING_VERIFY_MESSAGE = 'Payment is still pending';
const PENDING_VERIFY_MAX_ATTEMPTS = 5;

function isPaymentStillPending(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }
  const response = error.response;
  if (!response || typeof response !== 'object' || !('data' in response)) {
    return false;
  }
  const data = response.data;
  if (!data || typeof data !== 'object' || !('message' in data)) {
    return false;
  }
  return data.message === PENDING_VERIFY_MESSAGE;
}

export function rechargeStatusAfterVerifyError(error: unknown): 'pending' | 'failed' {
  return isPaymentStillPending(error) ? 'pending' : 'failed';
}

/**
 * Wallet Service
 * Handles wallet balance, transaction history, and recharge operations
 */
export const walletService = {
  /**
   * Get current wallet balance for the logged-in user
   * @returns Wallet with current balance
   */
  getBalance: async (): Promise<Wallet> => {
    const response = await api.get<Wallet>(API_ENDPOINTS.WALLET.BALANCE);
    return response.data;
  },

  /**
   * Get wallet transaction history with pagination
   * @param params - Pagination and filtering parameters
   * @returns Paginated list of wallet transactions
   */
  getTransactionHistory: async (
    params?: GetWalletTransactionsParams
  ): Promise<PaginatedResponse<WalletTransaction>> => {
    const response = await api.get<PaginatedResponse<WalletTransaction>>(
      API_ENDPOINTS.WALLET.TRANSACTIONS,
      {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          type: params?.type,
        },
      }
    );
    return response.data;
  },

  /**
   * Get wallet history with pagination (newer endpoint)
   * @param params - Pagination and filtering parameters
   * @returns Wallet history with pagination metadata
   */
  getHistory: async (params?: GetWalletHistoryParams): Promise<WalletHistoryResponse> => {
    const response = await api.get<WalletHistoryResponse>(API_ENDPOINTS.WALLET.HISTORY, {
      params: {
        page: params?.page || 1,
        limit: params?.limit || 20,
        type: params?.type,
        startDate: params?.startDate,
        endDate: params?.endDate,
      },
    });
    return response.data;
  },

  /**
   * Initiate wallet recharge (creates payment order)
   * @param data - Recharge amount
   * @returns Payment order details (orderId, paymentSessionId, amount)
   */
  initiateRecharge: async (data: RechargeRequest): Promise<{ orderId: string; paymentSessionId: string; amount: number }> => {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: { orderId: string; paymentSessionId: string; amount: number };
    }>(API_ENDPOINTS.WALLET.RECHARGE, data);
    const payload = response.data?.data;
    if (!payload?.orderId || !payload?.paymentSessionId) {
      throw new Error('Payment session was not created');
    }
    return payload;
  },

  /**
   * Verify payment after user completes payment gateway flow
   * @param orderId - Cashfree order ID
   * @returns Payment verification result with status, amount, and updated balance
   */
  verifyPayment: async (orderId: string): Promise<{ status: string; amount: number; balance: number; walletTransactionId?: string }> => {
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await api.post<{
          success: boolean;
          message: string;
          data: { status: string; amount: number; balance: number; walletTransactionId?: string };
        }>(API_ENDPOINTS.WALLET.VERIFY_PAYMENT, { orderId });
        const payload = response.data?.data;
        if (!payload?.status) {
          throw new Error('Payment verification returned no result');
        }
        return payload;
      } catch (error) {
        if (!isPaymentStillPending(error) || attempt >= PENDING_VERIFY_MAX_ATTEMPTS - 1) {
          throw error;
        }
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 1000 * 2 ** attempt);
        });
      }
    }
  },
};
